import MojitoCore from 'mojito/core';
import { STORE_KEY, initialState } from './settled-bets-slice.js';
import ServicesTypes from 'services/common/types.js';

const reduxInstance = MojitoCore.Services.redux;
const { CONTENT_STATE } = ServicesTypes;

/**
 * Settled Bets related selectors.
 *
 * @module SettledBetsSelectors
 * @name settledBetsSelectors
 * @memberof Mojito.Services.Bets
 */

/**
 * Selects bets store state.
 *
 * @function selectState
 *
 * @param {object} [state] - Application state object. If not provided then state from global {@link Mojito.Core.Services.redux|redux store} will be returned.
 * @returns {Mojito.Services.Bets.SettledBetsState} Settled Bets store state.
 * @memberof Mojito.Services.Bets.settledBetsSelectors
 */
export const selectState = state => {
    // Should be no need to fallback to global state once fully migrated to Redux.
    const appState = state || reduxInstance.store?.getState();
    return appState?.[STORE_KEY] || initialState;
};

/**
 * Selects data hash state.
 *
 * @function selectDataHash
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {string} Data hash.
 * @memberof Mojito.Services.Bets.settledBetsSelectors
 */
export const selectDataHash = state => selectState(state).dataHash;

/**
 * Selects if settled bets have been requested.
 *
 * @function selectIsRequested
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {boolean} True if settled bets have been requested.
 * @memberof Mojito.Services.Bets.settledBetsSelectors
 */
export const selectIsRequested = state => selectState(state).isRequested;

/**
 * Check if settled bets fetching is in progress.
 *
 * @function selectIsPending
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {boolean} True if settled bets fetch is in progress.
 * @memberof Mojito.Services.Bets.settledBetsSelectors
 */
export const selectIsPending = state => selectState(state).loadingStatus === CONTENT_STATE.PENDING;

/**
 * Selects bets.
 *
 * @function selectBets
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {Array<Mojito.Services.Bets.types.Bet>} Bets.
 * @memberof Mojito.Services.Bets.settledBetsSelectors
 */
export const selectBets = state => selectState(state).bets;
